import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import "./registerServiceWorker";
import i18n from "./i18n";
import Package from "../package.json";

// 3rd party
import axios from "axios";
import VueAxios from "vue-axios";
import VueCookies from "vue-cookies";
import { Loader } from "@googlemaps/js-api-loader";
import VueTelInput from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import VueMasonry from "vue-masonry-css";
import VueTelInputVuetify from "vue-tel-input-vuetify/lib";
import VueNativeNotification from "vue-native-notification";
import VueMeta from "vue-meta";
import { VueMaskDirective } from "v-mask";

// Plugins
import Alert from "./plugins/alert";
import Translation from "./plugins/translation";
import Helper from "./plugins/helper";

// Layouts
import CenteredColumn from "./components/layouts/CenteredColumn.vue";
import Fullscreen from "./components/layouts/Fullscreen.vue";
import OneColumn from "./components/layouts/OneColumn.vue";
import TwoColumns from "./components/layouts/TwoColumns.vue";

// Overlays
import Alerts from "./components/overlays/Alerts.vue";
import Env from "./components/overlays/Env.vue";
import vuetify from "./plugins/vuetify";
import EscapeHtml from "./plugins/escape-html";
import PrettyBytes from "./plugins/pretty-bytes";
import FullName from "./plugins/fullname";
import PhoneNumber from "./plugins/phone";
import Connectors from "./plugins/connectors";
import { bus } from "./bus";

// Utils
import { phoneNumberFormater as formatPhoneNumber } from "./common/utils";
import { PASSWORD_FLOW_ROUTES } from "./common/constants";

// Sementic Version
export const version = Package.version;

// Filters
Vue.filter("split", (text, separator, start, end) => {
	return text.split(separator).splice(start, end).join(separator);
});

Vue.filter("truncate", (text, length = 15) => {
	return (text.length > length ? `${text.substring(0, length)}...` : text);
});

// PSH Format stands for (parentheses-space-hyphen formatting)
Vue.filter("phone", function (phoneNumber) {
	return formatPhoneNumber(phoneNumber);
});

Vue.filter("br", (text) => {
	return text.replace("\n", "<br />");
});

// Array Prototypres
Array.prototype.move = function (from, to) {
	this.splice(to, 0, this.splice(from, 1)[0]);
};

// Styles
import "@/assets/css/index.scss";

// Configuration
Vue.config.productionTip = false;

// Load layouts
Vue.component("CenteredColumn", CenteredColumn);
Vue.component("Fullscreen", Fullscreen);
Vue.component("OneColumn", OneColumn);
Vue.component("TwoColumns", TwoColumns);

// Load overlays
Vue.component("Alerts", Alerts);
Vue.component("Env", Env);

// Plugins
Vue.use(EscapeHtml);
Vue.use(PrettyBytes);
Vue.use(FullName);
Vue.use(PhoneNumber);

// External components
Vue.use(VueTelInput);
Vue.use(VueTelInputVuetify, { vuetify });
Vue.use(VueNativeNotification);

// Chrome & Firefox only accept cookies for < 400 days validity
Vue.use(VueCookies, { expires: "365d" });

Vue.directive("mask", VueMaskDirective);

// Redirect old urls
switch (window.location.hostname) {
	case "app.bucco.me":
		window.location.href = "https://my.fresk.app";
		break;

	case "rc.bucco.me":
		window.location.href = "https://rc.fresk.app";
		break;
}

// Axios
axios.defaults.baseURL = `${process.env.VUE_APP_API_URL}/`;
axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
	config.timeout = 90000;
	config.headers.Authorization = `Bearer ${store.state.login.jwt}`;
	return config;
});

let refreshPromise = null;

axios.interceptors.response.use(
	(response) => {
		// Always make sure refreshPromise is reset
		refreshPromise = null;
		return response;
	},
	async (error) => {
		const { response } = error;
		const originalRequest = error.config;

		// Silence logout error
		if (originalRequest?.url?.includes("/auth/logout")) {
			await store.dispatch("login/showLogin");
			return new Promise(() => {});
		}

		// Check if we're on a password-related route that should handle errors inline
		const isPasswordRoute = PASSWORD_FLOW_ROUTES.includes(router.currentRoute.path);

		switch (response?.status) {
			case 401:
				// Let passkey login 401s propagate to the caller directly
				if (error.config?.url?.includes("/auth/login/webauthn/")) {
					return Promise.reject(error);
				}

				if (error.config &&
					store.getters["login/connected"] &&
					!error.config.url.includes("/auth/refresh") &&
					!error.config.url.includes("/auth/profile")) {
					if (!refreshPromise) { // check for an existing in-progress request
						refreshPromise = store.dispatch("login/refreshToken")
							.finally (() => {
								refreshPromise = null;
							});
					}

					return refreshPromise
						?.then(token => {
							if (token?.length) {
								error.config.headers.Authorization = `Bearer ${token}`;
								return axios.request(error.config);
							}
							return new Promise(() => {});
						});
				}
				// Don't redirect to login if we're on a password-related route
				if (!isPasswordRoute) {
					await store.dispatch("login/sessionExpired");
				}
				return Promise.reject(error);

			case 440:
				if (!originalRequest._rt && store.state.login.jwt?.length) {
					// retry in case the call was called during refresh token
					originalRequest._rt = true;
					originalRequest.headers.Authorization = `Bearer ${store.state.login.jwt}`;
					return axios.request(originalRequest);
				}

				// Don't redirect to login if we're on a password-related route
				if (!isPasswordRoute) {
					await store.dispatch("login/sessionExpired");
					bus.$emit("disconnected");
				}
				return Promise.reject(error);
		}

		return Promise.reject(error);
	}
);

Vue.use(VueAxios, axios);

// 3rd party
Vue.use(VueMasonry);

// Plugins
Vue.use(Alert);
Vue.use(Translation);
Vue.use(Helper);
Vue.use(VueMeta);
Vue.use(Connectors, { store });

export { bus };

// VueJs Application
export const Main = new Vue({
	i18n,

	async mounted() {
		if (store.state?.login?.jwt) {
			await store.dispatch("login/onRefresh");
		}

		window.setInterval(async () => {
			if ("serviceWorker" in navigator) {
				try {
					const reg = await navigator.serviceWorker.getRegistration();

					if (reg) {
						reg.update();
					}
				} catch (err) {
					console.warn("Could not get service worker registration:", err);
				}
			}
		}, 60000);

		// Setup Google API loader
		const loader = new Loader({
			apiKey: process.env.VUE_APP_GMAP_API_KEY,
			language: this.$i18n.locale,
			libraries: ["places"],
			version: "quarterly"
		});

		await loader.load();
	},

	render: (h) => h(App),
	router,
	store,
	vuetify
}).$mount("#app");
